import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCreateDelegation } from "@/hooks";

interface DelegateFormDialogProps {
  open: boolean;
  onClose: () => void;
  processId: string;
  formId: string;
  applicantProcessId: string;
}

interface RecipientRow {
  email: string;
  name: string;
  role: "EDITOR" | "VIEWER";
}

const EXPIRY_OPTIONS = [7, 14, 30];

const emptyRow = (role: RecipientRow["role"]): RecipientRow => ({
  email: "",
  name: "",
  role,
});

const DelegateFormDialog: React.FC<DelegateFormDialogProps> = ({
  open,
  onClose,
  processId,
  formId,
  applicantProcessId,
}) => {
  const { t } = useTranslation();
  const createDelegation = useCreateDelegation();

  // The first person defaults to the editor; the rule is enforced below.
  const [rows, setRows] = useState<RecipientRow[]>([emptyRow("EDITOR")]);
  const [message, setMessage] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(14);

  const patchRow = (i: number, patch: Partial<RecipientRow>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Making one person the editor demotes whoever held it — there is only ever one.
  const setEditor = (i: number) =>
    setRows((prev) =>
      prev.map((r, idx) => ({ ...r, role: idx === i ? "EDITOR" : "VIEWER" }))
    );

  const addRow = () => setRows((prev) => [...prev, emptyRow("VIEWER")]);
  const removeRow = (i: number) =>
    setRows((prev) => prev.filter((_, idx) => idx !== i));

  const reset = () => {
    setRows([emptyRow("EDITOR")]);
    setMessage("");
    setExpiresInDays(14);
  };

  const handleSend = async () => {
    const recipients = rows
      .map((r) => ({
        email: r.email.trim().toLowerCase(),
        name: r.name.trim() || undefined,
        role: r.role,
      }))
      .filter((r) => r.email);

    if (recipients.length === 0) {
      toast.error(t("delegation.owner.externalEmail"));
      return;
    }
    if (recipients.filter((r) => r.role === "EDITOR").length !== 1) {
      toast.error(t("delegation.owner.oneEditorRule"));
      return;
    }
    const emails = recipients.map((r) => r.email);
    if (new Set(emails).size !== emails.length) {
      toast.error(t("delegation.owner.duplicateEmail"));
      return;
    }

    try {
      await createDelegation.mutateAsync({
        processId,
        formId,
        applicantProcessId,
        message: message.trim() || undefined,
        expiresInDays,
        recipients,
      });
      toast.success(t("delegation.owner.created"));
      reset();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : t("delegation.owner.createFailed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[620px] sm:rounded-md">
        <DialogHeader>
          <DialogTitle>{t("delegation.owner.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("delegation.owner.dialogDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">
              {t("delegation.owner.recipients")}
            </div>
            {rows.map((row, i) => (
              <div
                key={i}
                className="flex flex-wrap items-center gap-2 rounded border border-gray-200 p-2"
              >
                <input
                  type="email"
                  className="main-input min-w-[180px] flex-1"
                  placeholder={t("delegation.owner.emailPlaceholder")}
                  value={row.email}
                  onChange={(e) => patchRow(i, { email: e.target.value })}
                />
                <input
                  type="text"
                  className="main-input min-w-[140px] flex-1"
                  placeholder={t("delegation.owner.namePlaceholder")}
                  value={row.name}
                  onChange={(e) => patchRow(i, { name: e.target.value })}
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setEditor(i)}
                    className={`rounded px-2 py-1 text-xs ${
                      row.role === "EDITOR"
                        ? "bg-darkBlue text-white"
                        : "border border-gray-300 text-gray-600"
                    }`}
                    title={t("delegation.owner.editorHint")}
                  >
                    {t("delegation.owner.editor")}
                  </button>
                  <button
                    type="button"
                    onClick={() => patchRow(i, { role: "VIEWER" })}
                    className={`rounded px-2 py-1 text-xs ${
                      row.role === "VIEWER"
                        ? "bg-darkBlue text-white"
                        : "border border-gray-300 text-gray-600"
                    }`}
                    title={t("delegation.owner.viewerHint")}
                  >
                    {t("delegation.owner.viewer")}
                  </button>
                </div>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    className="text-gray-400 hover:text-red-600"
                    title={t("delegation.owner.removeRecipient")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addRow}
              className="flex items-center gap-1 text-sm text-darkBlue"
            >
              <Plus className="h-4 w-4" />
              {t("delegation.owner.addRecipient")}
            </button>
            <p className="text-xs text-gray-500">
              {t("delegation.owner.oneEditorRule")}
            </p>
          </div>

          <div className="space-y-1">
            <div className="text-sm font-medium">
              {t("delegation.owner.message")}
            </div>
            <textarea
              className="main-input min-h-[80px] w-full"
              placeholder={t("delegation.owner.messagePlaceholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">
              {t("delegation.owner.expiresIn")}
            </span>
            <select
              className="main-input w-auto"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
            >
              {EXPIRY_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {t("delegation.owner.days", { count: d })}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            className="rounded border px-4 py-2 text-sm"
            onClick={onClose}
          >
            {t("delegation.cancel")}
          </button>
          <button
            type="button"
            className="main-dark-button text-sm"
            onClick={handleSend}
            disabled={createDelegation.isPending}
          >
            {createDelegation.isPending
              ? t("delegation.owner.sending")
              : t("delegation.owner.send")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DelegateFormDialog;
